// ./src/App.tsx

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CircularProgress, Box, Typography } from '@mui/material';

// Lazy loaded components for better performance
const Dashboard = React.lazy(() => import('./components/Dashboard/Dashboard'));
const StockSummaryDashboard = React.lazy(() => import('./components/Dashboard/StockSummaryDashboard'));
const TanlabStockDashboard = React.lazy(() => import('./components/Dashboard/TanlabStockDashboard'));
const UnitConverterDashboard = React.lazy(() => import('./components/Dashboard/UnitConverterDashboard'));
const ProductionCalculator = React.lazy(() => import('./components/Dashboard/ProductionCalculator'));
const ProductionPlanning = React.lazy(() => import('./components/Dashboard/ProductionPlanning'));
const ProductionPlanningNew = React.lazy(() => import('./components/Dashboard/ProductionPlanningNew'));
const EditProductionPlan = React.lazy(() => import('./components/Dashboard/EditProductionPlan'));
const UnifiedMonthlyPlanning = React.lazy(() => import('./components/Dashboard/UnifiedMonthlyPlanning'));
const SalesMonthlyPlanning = React.lazy(() => import('./components/Dashboard/SalesMonthlyPlanning'));
const ProductionTimeline = React.lazy(() => import('./components/Dashboard/ProductionTimeline'));
const FormulaManager = React.lazy(() => import('./components/Dashboard/FormulaManager'));
const StockRunwayDashboard = React.lazy(() => import('./components/Dashboard/StockRunwayDashboard'));
const SalesAnalysisDashboard = React.lazy(() => import('./components/Dashboard/SalesAnalysisDashboard'));
const SupabaseInventoryDashboard = React.lazy(() => import('./components/Dashboard/SupabaseInventoryDashboard'));
const StockMovementDashboard = React.lazy(() => import('./components/Dashboard/StockMovementDashboard'));
const ApprovalPage = React.lazy(() => import('./components/Dashboard/ApprovalPage'));
const DashboardPlanning = React.lazy(() => import('./components/Dashboard/DashboardPlanning'));

// Lazy loaded pages
const ReviewPage = React.lazy(() => import('./pages/ReviewPage'));
const WarehousePage = React.lazy(() => import('./pages/WarehousePage'));

// Error Boundary
import { DashboardErrorBoundary } from './components/Dashboard/DashboardErrorBoundary';
import AppShell from './components/Layout/AppShell';

// Loading component
const LoadingSpinner = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="60vh"
    gap={2}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      Loading...
    </Typography>
  </Box>
);

const queryClient = new QueryClient({
  // คุณสามารถกำหนด defaultOptions, retries ฯลฯ ได้
});

// Create a default Material-UI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      dark: '#1565c0',
      light: '#42a5f5',
    },
    secondary: {
      main: '#dc004e',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

const App: React.FC = () => {
  return (
    <DashboardErrorBoundary>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <AppShell>
              <Routes>
            {/* หน้าแรก "/" */}
            <Route path="/" element={
              <Suspense fallback={<LoadingSpinner />}>
                <Dashboard />
              </Suspense>
            } />

            {/* Stock JLC Page */}
            <Route path="/inventory" element={
              <Suspense fallback={<LoadingSpinner />}>
                <StockSummaryDashboard database="JLC" />
              </Suspense>
            } />

            {/* TANLAB Main Dashboard */}
            <Route path="/tanlab" element={
              <Suspense fallback={<LoadingSpinner />}>
                <TanlabStockDashboard />
              </Suspense>
            } />

            {/* TANLAB Stock Summary */}
            <Route path="/tanlab/stock-summary" element={
              <Suspense fallback={<LoadingSpinner />}>
                <StockSummaryDashboard database="TANLAB_CSMILE" />
              </Suspense>
            } />

            {/* Stock Movement Page - Stock Card System */}
            <Route path="/stock-movement" element={
              <Suspense fallback={<LoadingSpinner />}>
                <StockMovementDashboard />
              </Suspense>
            } />

            {/* TANLAB Production Calculator */}
            <Route path="/tanlab/production" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionCalculator />
              </Suspense>
            } />

            {/* TANLAB Production Planning */}
            <Route path="/production-planning" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionPlanning />
              </Suspense>
            } />
            <Route path="/production-planning-new" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionPlanningNew />
              </Suspense>
            } />
            <Route path="/production-planning-new/edit/:planId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <EditProductionPlan />
              </Suspense>
            } />

            {/* 🎯 Unified Monthly Planning - All-in-One System */}
            <Route path="/production-planning/monthly" element={
              <Suspense fallback={<LoadingSpinner />}>
                <UnifiedMonthlyPlanning />
              </Suspense>
            } />

            {/* 🎯 NEW SEPARATED SYSTEMS - Sales vs Production */}

            {/* Sales Monthly Planning (ฝั่งขาย) */}
            <Route path="/monthly-planning" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SalesMonthlyPlanning />
              </Suspense>
            } />
            <Route path="/monthly-planning/:year?/:month?" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SalesMonthlyPlanning />
              </Suspense>
            } />

            {/* Daily Production Planning (ฝั่งโรงงาน)
                หมายเหตุ: UI และ route ถูกปิดไม่ให้ใช้งานในตอนนี้ตามคำขอผู้ใช้
                หากต้องการเปิดใช้งานอีกครั้ง ให้ uncomment route ชุดด้านล่าง

            <Route path="/daily-production" element={
              <Suspense fallback={<LoadingSpinner />}>
                <DailyProductionPlanning />
              </Suspense>
            } />
            <Route path="/daily-production/:date?" element={
              <Suspense fallback={<LoadingSpinner />}>
                <DailyProductionPlanning />
              </Suspense>
            } />
            */}

            {/* 🎯 NEW TIMELINE VIEW - Microsoft Project Style */}
            <Route path="/production-timeline" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionTimeline />
              </Suspense>
            } />
            <Route path="/production-timeline/:date?" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionTimeline />
              </Suspense>
            } />

            {/* 📅 Calendar View - Default to Calendar Mode */}
            <Route path="/calendar" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionTimeline initialView="calendar" />
              </Suspense>
            } />
            <Route path="/calendar/:date?" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ProductionTimeline initialView="calendar" />
              </Suspense>
            } />

            {/* ✅ Approval Page - อนุมัติแผนการผลิต */}
            <Route path="/approval" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ApprovalPage />
              </Suspense>
            } />

            {/* Legacy redirect - forward old routes to new unified system */}
            <Route path="/production-planning/monthly/:year?/:month?" element={
              <Suspense fallback={<LoadingSpinner />}>
                <UnifiedMonthlyPlanning />
              </Suspense>
            } />


            {/* TANLAB Formula Manager */}
            <Route path="/tanlab/formulas" element={
              <Suspense fallback={<LoadingSpinner />}>
                <FormulaManager />
              </Suspense>
            } />

            {/* Unit Converter Page */}
            <Route path="/unit-converter" element={
              <Suspense fallback={<LoadingSpinner />}>
                <UnitConverterDashboard />
              </Suspense>
            } />

            {/* Dashboard Planning - ภาพรวมกระบวนการวางแผนการผลิต */}
            <Route path="/dashboard-planning" element={
              <Suspense fallback={<LoadingSpinner />}>
                <DashboardPlanning />
              </Suspense>
            } />

            {/* Stock Runway Page - JLC */}
            <Route path="/stock-runway" element={
              <Suspense fallback={<LoadingSpinner />}>
                <StockRunwayDashboard />
              </Suspense>
            } />

            {/* Sales Analysis Page - วิเคราะห์ยอดขายและ Stock Runway */}
            <Route path="/sales-analysis" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SalesAnalysisDashboard />
              </Suspense>
            } />

            {/* Supabase Inventory Page - Stock Inventory จาก Supabase พร้อม Stock Runway */}
            <Route path="/supabase-inventory" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SupabaseInventoryDashboard />
              </Suspense>
            } />
            <Route path="/superbase-inventory" element={
              <Suspense fallback={<LoadingSpinner />}>
                <SupabaseInventoryDashboard />
              </Suspense>
            } /> {/* Alias สำหรับพิมพ์ผิด */}


            {/* Review Page => /review/:itemId */}
            <Route path="/review/:itemId" element={
              <Suspense fallback={<LoadingSpinner />}>
                <ReviewPage />
              </Suspense>
            } />

            {/* Warehouse Management Page - 4 ฟีเจอร์: Finance, Stock Card, Transfer, Packing */}
            <Route path="/warehouse" element={
              <Suspense fallback={<LoadingSpinner />}>
                <WarehousePage />
              </Suspense>
            } />

          </Routes>
        </AppShell>
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
    </DashboardErrorBoundary>
  );
};

export default App;
